/**
 * Feature Tests Service
 * 
 * Defines comprehensive test cases for all Plot Planet features.
 * Includes Firestore operations for persisting test results per project.
 */

import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../firebase'

// ============================================
// Collection Configuration
// ============================================

const TEST_RESULTS_COLLECTION = 'testResults'

/**
 * Build collection path for test results with project scoping
 */
function getTestResultsPath(projectId) {
  if (!projectId) {
    return TEST_RESULTS_COLLECTION
  }
  return `projects/${projectId}/${TEST_RESULTS_COLLECTION}`
}

// ============================================
// Test Status Constants
// ============================================

export const TEST_STATUS = {
  UNTESTED: 'untested',
  IN_PROGRESS: 'in_progress',
  PASSED: 'passed',
  FAILED: 'failed',
}

export const TEST_PRIORITY = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
}

// ============================================
// Test Categories
// ============================================

export const TEST_CATEGORIES = {
  authentication: {
    id: 'authentication',
    name: 'Authentication',
    description: 'User authentication and session management',
    iconName: 'lock',
    order: 1,
  },
  projects: {
    id: 'projects',
    name: 'Project Management',
    description: 'Project creation, switching, and member management',
    iconName: 'projects',
    order: 2,
  },
  entityCrud: {
    id: 'entityCrud',
    name: 'Entity CRUD',
    description: 'Create, read, update, delete operations for all entity types',
    iconName: 'synopsis',
    order: 3,
  },
  entityFeatures: {
    id: 'entityFeatures',
    name: 'Entity Features',
    description: 'Tier filtering, image upload, entity linking',
    iconName: 'design',
    order: 4,
  },
  views: {
    id: 'views',
    name: 'Views',
    description: 'Timeline, synopsis, storyboard, prompts, images views',
    iconName: 'eye',
    order: 5,
  },
  importExport: {
    id: 'importExport',
    name: 'Import/Export',
    description: 'JSON import, data parsing, validation',
    iconName: 'import',
    order: 6,
  },
  firebase: {
    id: 'firebase',
    name: 'Firebase Integration',
    description: 'Real-time sync, data persistence, storage',
    iconName: 'fire',
    order: 7,
  },
  uiComponents: {
    id: 'uiComponents',
    name: 'UI Components',
    description: 'Modals, buttons, cards, glass panels',
    iconName: 'prompts',
    order: 8,
  },
}

// ============================================
// Feature Test Definitions
// ============================================

export const FEATURE_TESTS = [
  // ----------------------------------------
  // Authentication Tests
  // ----------------------------------------
  {
    id: 'auth-login',
    category: 'authentication',
    name: 'User Login',
    description: 'User can log in with valid email and password',
    steps: [
      'Navigate to login page',
      'Enter valid email and password',
      'Click login button',
      'Verify redirect to projects page',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'auth-signup',
    category: 'authentication',
    name: 'User Signup',
    description: 'New user can create an account',
    steps: [
      'Navigate to signup page',
      'Enter email and password',
      'Submit registration form',
      'Verify account creation and auto-login',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'auth-logout',
    category: 'authentication',
    name: 'User Logout',
    description: 'User can log out and session is cleared',
    steps: [
      'Click logout button in navigation',
      'Verify redirect to login page',
      'Verify session is cleared',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'auth-session',
    category: 'authentication',
    name: 'Session Persistence',
    description: 'User session persists across page reloads',
    steps: [
      'Log in to the application',
      'Refresh the page',
      'Verify user remains logged in',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'auth-protected-routes',
    category: 'authentication',
    name: 'Protected Routes',
    description: 'Unauthenticated users cannot access protected pages',
    steps: [
      'Log out of the application',
      'Try to navigate directly to a project page',
      'Verify redirect to login page',
    ],
    priority: TEST_PRIORITY.HIGH,
  },

  // ----------------------------------------
  // Project Management Tests
  // ----------------------------------------
  {
    id: 'project-create',
    category: 'projects',
    name: 'Create Project',
    description: 'User can create a new project',
    steps: [
      'Navigate to projects list',
      'Click create project button',
      'Enter project name and description',
      'Submit form and verify project appears in list',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'project-list',
    category: 'projects',
    name: 'List Projects',
    description: 'User sees all projects they have access to',
    steps: [
      'Navigate to projects list',
      'Verify all accessible projects are displayed',
      'Verify role badges are shown correctly',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'project-switch',
    category: 'projects',
    name: 'Switch Project',
    description: 'User can switch between projects',
    steps: [
      'Open a project',
      'Navigate back to projects list',
      'Select a different project',
      'Verify new project data is loaded',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'project-edit',
    category: 'projects',
    name: 'Edit Project',
    description: 'Admin can edit project details',
    steps: [
      'Open project settings',
      'Modify project name and description',
      'Save changes',
      'Verify changes are persisted',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'project-delete',
    category: 'projects',
    name: 'Delete Project',
    description: 'Admin can delete a project',
    steps: [
      'Open project settings',
      'Click delete project button',
      'Confirm deletion',
      'Verify project is removed from list',
    ],
    priority: TEST_PRIORITY.LOW,
  },
  {
    id: 'project-members-add',
    category: 'projects',
    name: 'Add Project Member',
    description: 'Admin can add members to a project',
    steps: [
      'Navigate to project members view',
      'Enter user email to invite',
      'Select role for new member',
      'Submit and verify member appears in list',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'project-members-role',
    category: 'projects',
    name: 'Change Member Role',
    description: 'Admin can change member roles',
    steps: [
      'Navigate to project members view',
      'Find existing member',
      'Change their role using dropdown',
      'Verify role change is saved',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'project-members-remove',
    category: 'projects',
    name: 'Remove Project Member',
    description: 'Admin can remove members from a project',
    steps: [
      'Navigate to project members view',
      'Click remove button for a member',
      'Confirm removal',
      'Verify member is removed from list',
    ],
    priority: TEST_PRIORITY.LOW,
  },

  // ----------------------------------------
  // Entity CRUD Tests
  // ----------------------------------------
  {
    id: 'character-create',
    category: 'entityCrud',
    name: 'Create Character',
    description: 'User can create a new character',
    steps: [
      'Navigate to characters view',
      'Click add character button',
      'Fill in character details',
      'Save and verify character appears in grid',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'character-read',
    category: 'entityCrud',
    name: 'View Character',
    description: 'User can view character details',
    steps: [
      'Navigate to characters view',
      'Click on a character card',
      'Verify modal shows all character details',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'character-update',
    category: 'entityCrud',
    name: 'Update Character',
    description: 'User can update character details',
    steps: [
      'Open character modal',
      'Modify character fields',
      'Save changes',
      'Verify changes are reflected on card',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'character-delete',
    category: 'entityCrud',
    name: 'Delete Character',
    description: 'User can delete a character',
    steps: [
      'Open character modal',
      'Click delete button',
      'Confirm deletion',
      'Verify character is removed from grid',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'location-create',
    category: 'entityCrud',
    name: 'Create Location',
    description: 'User can create a new location',
    steps: [
      'Navigate to locations view',
      'Click add location button',
      'Fill in location details',
      'Save and verify location appears in grid',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'location-update',
    category: 'entityCrud',
    name: 'Update Location',
    description: 'User can update location details',
    steps: [
      'Open location modal',
      'Modify location fields',
      'Save changes',
      'Verify changes are reflected on card',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'location-delete',
    category: 'entityCrud',
    name: 'Delete Location',
    description: 'User can delete a location',
    steps: [
      'Open location modal',
      'Click delete button',
      'Confirm deletion',
      'Verify location is removed from grid',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'event-create',
    category: 'entityCrud',
    name: 'Create Event',
    description: 'User can create a new event',
    steps: [
      'Navigate to events view',
      'Click add event button',
      'Fill in event details including date',
      'Save and verify event appears in grid',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'event-update',
    category: 'entityCrud',
    name: 'Update Event',
    description: 'User can update event details',
    steps: [
      'Open event modal',
      'Modify event fields',
      'Save changes',
      'Verify changes are reflected on card',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'event-delete',
    category: 'entityCrud',
    name: 'Delete Event',
    description: 'User can delete an event',
    steps: [
      'Open event modal',
      'Click delete button',
      'Confirm deletion',
      'Verify event is removed from grid',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'shot-create',
    category: 'entityCrud',
    name: 'Create Shot',
    description: 'User can create a new shot',
    steps: [
      'Navigate to shots view',
      'Click add shot button',
      'Fill in shot details',
      'Save and verify shot appears in grid',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'shot-update',
    category: 'entityCrud',
    name: 'Update Shot',
    description: 'User can update shot details',
    steps: [
      'Open shot modal',
      'Modify shot fields',
      'Save changes',
      'Verify changes are reflected on card',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'shot-delete',
    category: 'entityCrud',
    name: 'Delete Shot',
    description: 'User can delete a shot',
    steps: [
      'Open shot modal',
      'Click delete button',
      'Confirm deletion',
      'Verify shot is removed from grid',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },

  // ----------------------------------------
  // Entity Features Tests
  // ----------------------------------------
  {
    id: 'tier-display',
    category: 'entityFeatures',
    name: 'Tier Badge Display',
    description: 'Entity cards display tier badges correctly',
    steps: [
      'Navigate to any entity view',
      'Verify tier badges (A/B/C) are visible on cards',
      'Verify badge colors match tier levels',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'tier-filter',
    category: 'entityFeatures',
    name: 'Tier Filtering',
    description: 'User can filter entities by tier',
    steps: [
      'Navigate to any entity view',
      'Toggle tier filter buttons',
      'Verify only selected tier entities are shown',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'tier-edit',
    category: 'entityFeatures',
    name: 'Edit Entity Tier',
    description: 'User can change entity tier in edit modal',
    steps: [
      'Open entity modal',
      'Change tier using dropdown',
      'Save changes',
      'Verify tier badge updates on card',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'image-upload',
    category: 'entityFeatures',
    name: 'Image Upload',
    description: 'User can upload images for entities',
    steps: [
      'Open entity modal',
      'Click image upload button',
      'Select an image file',
      'Verify image is uploaded and displayed',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'image-extra',
    category: 'entityFeatures',
    name: 'Extra Images',
    description: 'User can add multiple images to an entity',
    steps: [
      'Open entity modal',
      'Navigate to extra images section',
      'Upload additional images',
      'Verify images appear in gallery',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'entity-linking',
    category: 'entityFeatures',
    name: 'Entity Linking',
    description: 'User can link entities to each other',
    steps: [
      'Open entity modal',
      'Navigate to links section',
      'Add links to other entities',
      'Save and verify links are persisted',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'entity-search',
    category: 'entityFeatures',
    name: 'Entity Search',
    description: 'User can search entities by name',
    steps: [
      'Navigate to any entity view',
      'Enter search query in search box',
      'Verify matching entities are shown',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },

  // ----------------------------------------
  // Views Tests
  // ----------------------------------------
  {
    id: 'view-timeline',
    category: 'views',
    name: 'Timeline View',
    description: 'Timeline view displays events chronologically',
    steps: [
      'Navigate to timeline view',
      'Verify events are displayed on timeline',
      'Test zoom and pan controls',
      'Verify event nodes are clickable',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'view-synopsis',
    category: 'views',
    name: 'Synopsis View',
    description: 'Synopsis view with markdown editor',
    steps: [
      'Navigate to synopsis view',
      'Verify markdown editor loads',
      'Edit synopsis content',
      'Verify changes are saved',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'view-storyboard',
    category: 'views',
    name: 'Storyboard View',
    description: 'Storyboard view with drag-drop shot ordering',
    steps: [
      'Navigate to storyboard view',
      'Verify shots are displayed in order',
      'Drag a shot to reorder',
      'Verify new order is saved',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'view-prompts',
    category: 'views',
    name: 'Prompts View',
    description: 'Prompt builder with presets and options',
    steps: [
      'Navigate to prompts view',
      'Select a prompt preset',
      'Toggle various prompt options',
      'Verify generated prompt updates',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'view-images',
    category: 'views',
    name: 'Images View',
    description: 'Images gallery with masonry layout',
    steps: [
      'Navigate to images view',
      'Verify images load in masonry grid',
      'Test filtering and search',
      'Click image to view details',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'view-characters',
    category: 'views',
    name: 'Characters View',
    description: 'Characters grid view with card layout',
    steps: [
      'Navigate to characters view',
      'Verify characters load in grid',
      'Test tier filters',
      'Click card to open modal',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'view-locations',
    category: 'views',
    name: 'Locations View',
    description: 'Locations grid view with card layout',
    steps: [
      'Navigate to locations view',
      'Verify locations load in grid',
      'Test tier filters',
      'Click card to open modal',
    ],
    priority: TEST_PRIORITY.HIGH,
  },

  // ----------------------------------------
  // Import/Export Tests
  // ----------------------------------------
  {
    id: 'import-characters',
    category: 'importExport',
    name: 'Import Characters JSON',
    description: 'User can import characters from JSON file',
    steps: [
      'Navigate to import view',
      'Select characters JSON file',
      'Review parsed data',
      'Confirm import and verify characters created',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'import-frames',
    category: 'importExport',
    name: 'Import Frames JSON',
    description: 'User can import frames/shots from JSON file',
    steps: [
      'Navigate to import view',
      'Select frames JSON file',
      'Review parsed data',
      'Confirm import and verify shots created',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'import-markdown',
    category: 'importExport',
    name: 'Import Markdown',
    description: 'User can import synopsis from markdown file',
    steps: [
      'Navigate to import view',
      'Select markdown file',
      'Review parsed content',
      'Confirm import and verify synopsis updated',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'import-validation',
    category: 'importExport',
    name: 'Import Validation',
    description: 'Invalid import data shows error messages',
    steps: [
      'Navigate to import view',
      'Select invalid/malformed file',
      'Verify error message is displayed',
      'Verify no data is imported',
    ],
    priority: TEST_PRIORITY.LOW,
  },

  // ----------------------------------------
  // Firebase Integration Tests
  // ----------------------------------------
  {
    id: 'firebase-realtime',
    category: 'firebase',
    name: 'Real-time Sync',
    description: 'Data updates sync in real-time across tabs',
    steps: [
      'Open app in two browser tabs',
      'Create entity in tab 1',
      'Verify entity appears in tab 2 without refresh',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'firebase-persistence',
    category: 'firebase',
    name: 'Data Persistence',
    description: 'Data persists after page reload',
    steps: [
      'Create a new entity',
      'Refresh the page',
      'Verify entity still exists',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'firebase-offline',
    category: 'firebase',
    name: 'Offline Support',
    description: 'App shows cached data when offline',
    steps: [
      'Load app with data',
      'Disconnect from network',
      'Verify cached data is still visible',
    ],
    priority: TEST_PRIORITY.LOW,
  },
  {
    id: 'firebase-storage',
    category: 'firebase',
    name: 'Storage Upload',
    description: 'Images upload to Firebase Storage correctly',
    steps: [
      'Upload an image',
      'Verify image URL is generated',
      'Verify image loads from storage URL',
    ],
    priority: TEST_PRIORITY.HIGH,
  },

  // ----------------------------------------
  // UI Components Tests
  // ----------------------------------------
  {
    id: 'ui-modal',
    category: 'uiComponents',
    name: 'Entity Modal',
    description: 'Entity modal opens and closes correctly',
    steps: [
      'Click on an entity card',
      'Verify modal opens with animation',
      'Click close button or backdrop',
      'Verify modal closes',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'ui-fab',
    category: 'uiComponents',
    name: 'Floating Action Button',
    description: 'FAB appears and triggers create action',
    steps: [
      'Navigate to any entity view',
      'Verify FAB is visible in corner',
      'Click FAB',
      'Verify create modal opens',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'ui-cards',
    category: 'uiComponents',
    name: 'Entity Cards',
    description: 'Entity cards display correctly with hover effects',
    steps: [
      'Navigate to any entity view',
      'Verify cards display with correct styling',
      'Hover over card',
      'Verify hover animation works',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'ui-glass',
    category: 'uiComponents',
    name: 'Glass Panel Effect',
    description: 'Glass morphism panels render correctly',
    steps: [
      'View any panel component',
      'Verify glass effect (blur, transparency)',
      'Verify text is readable on glass background',
    ],
    priority: TEST_PRIORITY.LOW,
  },
  {
    id: 'ui-navigation',
    category: 'uiComponents',
    name: 'Navigation',
    description: 'Navigation menu works correctly',
    steps: [
      'Click each navigation item',
      'Verify correct view loads',
      'Verify active state is highlighted',
    ],
    priority: TEST_PRIORITY.HIGH,
  },
  {
    id: 'ui-responsive',
    category: 'uiComponents',
    name: 'Responsive Layout',
    description: 'UI adapts to different screen sizes',
    steps: [
      'View app on desktop size',
      'Resize to tablet size',
      'Resize to mobile size',
      'Verify layout adapts at each breakpoint',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
  {
    id: 'ui-dark-theme',
    category: 'uiComponents',
    name: 'Dark Theme',
    description: 'Dark theme is applied consistently',
    steps: [
      'Navigate through all views',
      'Verify dark theme is applied',
      'Verify no white/light flashes',
      'Verify text contrast is adequate',
    ],
    priority: TEST_PRIORITY.MEDIUM,
  },
]

// ============================================
// Firestore Operations
// ============================================

/**
 * Get all test results for a project
 * @param {string|null} projectId - The project ID
 * @returns {Promise<object>} - Map of testId to result data
 */
export async function getTestResults(projectId) {
  const collectionPath = getTestResultsPath(projectId)
  const snapshot = await getDocs(collection(db, collectionPath))
  
  const results = {}
  snapshot.forEach((doc) => {
    results[doc.id] = { id: doc.id, ...doc.data() }
  })
  
  return results
}

/**
 * Save or update a test result
 * @param {string|null} projectId - The project ID
 * @param {object} testResult - Test result data
 * @returns {Promise<void>}
 */
export async function saveTestResult(projectId, testResult) {
  if (!testResult.testId) {
    throw new Error('testId is required')
  }
  
  const collectionPath = getTestResultsPath(projectId)
  const docRef = doc(db, collectionPath, testResult.testId)
  
  await setDoc(docRef, {
    ...testResult,
    lastUpdated: serverTimestamp(),
  }, { merge: true })
}

/**
 * Reset all test results for a project
 * @param {string|null} projectId - The project ID
 * @returns {Promise<void>}
 */
export async function resetAllTests(projectId) {
  const collectionPath = getTestResultsPath(projectId)
  const snapshot = await getDocs(collection(db, collectionPath))
  
  const batch = writeBatch(db)
  snapshot.forEach((doc) => {
    batch.delete(doc.ref)
  })
  
  await batch.commit()
}

/**
 * Seed initial test results (all untested)
 * @param {string|null} projectId - The project ID
 * @returns {Promise<void>}
 */
export async function seedTestResults(projectId) {
  const collectionPath = getTestResultsPath(projectId)
  const batch = writeBatch(db)
  
  FEATURE_TESTS.forEach((test) => {
    const docRef = doc(db, collectionPath, test.id)
    batch.set(docRef, {
      testId: test.id,
      status: TEST_STATUS.UNTESTED,
      lastUpdated: serverTimestamp(),
    })
  })
  
  await batch.commit()
}

/**
 * Get aggregated test statistics
 * @param {string|null} projectId - The project ID
 * @returns {Promise<object>} - Statistics object
 */
export async function getTestStats(projectId) {
  const results = await getTestResults(projectId)
  
  const stats = {
    total: FEATURE_TESTS.length,
    passed: 0,
    failed: 0,
    inProgress: 0,
    untested: 0,
    byCategory: {},
  }
  
  // Initialize category stats
  Object.keys(TEST_CATEGORIES).forEach((catId) => {
    stats.byCategory[catId] = {
      total: 0,
      passed: 0,
      failed: 0,
      inProgress: 0,
      untested: 0,
    }
  })
  
  // Count by test
  FEATURE_TESTS.forEach((test) => {
    const result = results[test.id]
    const status = result?.status || TEST_STATUS.UNTESTED
    
    // Update overall stats
    switch (status) {
      case TEST_STATUS.PASSED:
        stats.passed++
        break
      case TEST_STATUS.FAILED:
        stats.failed++
        break
      case TEST_STATUS.IN_PROGRESS:
        stats.inProgress++
        break
      default:
        stats.untested++
    }
    
    // Update category stats
    if (stats.byCategory[test.category]) {
      stats.byCategory[test.category].total++
      switch (status) {
        case TEST_STATUS.PASSED:
          stats.byCategory[test.category].passed++
          break
        case TEST_STATUS.FAILED:
          stats.byCategory[test.category].failed++
          break
        case TEST_STATUS.IN_PROGRESS:
          stats.byCategory[test.category].inProgress++
          break
        default:
          stats.byCategory[test.category].untested++
      }
    }
  })
  
  // Calculate percentages
  stats.passRate = stats.total > 0 ? Math.round((stats.passed / stats.total) * 100) : 0
  stats.completionRate = stats.total > 0 
    ? Math.round(((stats.passed + stats.failed) / stats.total) * 100) 
    : 0
  
  return stats
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get tests by category
 * @param {string} categoryId - The category ID
 * @returns {array} - Array of tests in that category
 */
export function getTestsByCategory(categoryId) {
  return FEATURE_TESTS.filter(test => test.category === categoryId)
}

/**
 * Get tests by priority
 * @param {string} priority - The priority level
 * @returns {array} - Array of tests with that priority
 */
export function getTestsByPriority(priority) {
  return FEATURE_TESTS.filter(test => test.priority === priority)
}

/**
 * Get a single test by ID
 * @param {string} testId - The test ID
 * @returns {object|undefined} - The test definition or undefined
 */
export function getTestById(testId) {
  return FEATURE_TESTS.find(test => test.id === testId)
}

/**
 * Get sorted categories for display
 * @returns {array} - Array of category objects sorted by order
 */
export function getSortedCategories() {
  return Object.values(TEST_CATEGORIES).sort((a, b) => a.order - b.order)
}
